import { useEffect } from 'react';
import { Box } from '@mui/material';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { CommunityPostCell } from './components/CommunityPostCell';
import { PostSkeleton } from './components/PostSkeleton';
import { CommunityLayout } from './components/CommunityLayout';
import { useCommunityPost } from './hooks/useCommunityPost';
import { useDeleteCommunityPost } from './hooks/useDeleteCommunityPost';
import { useToggleReactionPost } from './hooks/useToggleReactionPost';
import { useDmFeatureEnabled } from './hooks/useDmFeatureEnabled';
import {
  useBlockUserMutation,
  useGetCommunityQuery,
} from '../../../generated/graphql';
import { communityQueryKeys } from './constants';
import { navigate, PATHS } from '@client/routes';
import auth from '@client/components/auth';
import { getReplyInDMHandler } from './utils/replyInDM';
import { communityAnalytics } from './utils/communityAnalytics';

type Props = {
  params: {
    influencerId: string;
    communityId: string;
    postId: string;
  };
};

export const PostPage = ({ params }: Props) => {
  const influencerId = Number(params.influencerId);
  const communityId = Number(params.communityId);
  const { postId } = params;

  const queryClient = useQueryClient();
  const { isDmFeatureEnabled } = useDmFeatureEnabled(influencerId);

  const {
    data: post,
    isInitialLoading,
    isFetchedAfterMount,
  } = useCommunityPost(communityId, postId);
  const { data: communityData } = useGetCommunityQuery({
    variables: { id: communityId },
  });
  const ownerName = communityData?.community?.owner.username || 'Creator';
  const isUserCommunityOwner =
    communityData?.community?.owner.badges.includes('own') || false;

  const deletePost = useDeleteCommunityPost(communityId);
  const togglePostReaction = useToggleReactionPost(communityId);
  const [blockUserMutation] = useBlockUserMutation();

  useEffect(() => {
    if (isFetchedAfterMount && !post) {
      toast.error('Post not found');
      navigate(PATHS.COMMUNITY(influencerId, communityId));
    }
  }, [isFetchedAfterMount, post, communityId, influencerId]);

  const onBlock = async (cId: number, userId: number) => {
    return blockUserMutation({ variables: { communityId: cId, userId } })
      .then(() => {
        toast.success('User blocked successfully');
        queryClient.refetchQueries({ queryKey: communityQueryKeys.posts(cId) });
        navigate(PATHS.COMMUNITY(influencerId, cId));
      })
      .catch(() => toast.error('Something went wrong with blocking user'));
  };

  const handleDeletePost = async () => {
    if (!post) return;
    communityAnalytics.trackPostDeleted(post, influencerId);
    await deletePost.mutateAsync(postId);
    navigate(PATHS.COMMUNITY(influencerId, communityId));
  };

  return (
    <CommunityLayout
      communityId={communityId}
      postId={postId}
      ownerName={ownerName}
      influencerId={influencerId}
    >
      <Box className="max-w-[680px] my-0 mx-auto bg-white rounded-2xl py-2.5 px-2.5">
        {isInitialLoading || !post ? (
          <PostSkeleton />
        ) : (
          <CommunityPostCell
            influencerId={influencerId}
            communityId={communityId}
            isUserCommunityOwner={isUserCommunityOwner}
            postData={post}
            defaultExpanded
            onDelete={handleDeletePost}
            onBlock={onBlock}
            onToggleReaction={(type) => {
              togglePostReaction.mutateAsync({ postId: post.id, type });
              communityAnalytics.trackReactionAddedOnPost(post, influencerId);
            }}
            onReplyInDM={getReplyInDMHandler({
              author: post.author,
              isOwner: isUserCommunityOwner,
              isAdminModerator: auth.isAdministrator(),
              influencerId,
              isInfluencer: auth.isInfluencer(),
              isDmFeatureEnabled,
            })}
            isDmFeatureEnabled={isDmFeatureEnabled}
          />
        )}
      </Box>
    </CommunityLayout>
  );
};
